import React, { useState, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Play, BookOpen, Share2 } from 'lucide-react';

const LibraryCard = ({ article, onPlaySummary, onPlayFull, onTextRead, onDelete, onShare, isPlayingSummary, isPlayingFull, darkMode = false }) => {
  return (
    <Card className={`overflow-hidden mb-4 shadow-sm hover:shadow-md transition-shadow ${darkMode ? 'bg-gray-800 border-gray-700' : ''}`}>
      {/* Article Image */}
      <div className="relative">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-48 object-cover"
        />
        {/* Category Badge and Share Icon */}
        <div className="absolute top-3 left-3 right-3 flex justify-between items-start">
          <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">
            {article.category}
          </span>
          <button
            onClick={() => onShare(article)}
            className="bg-white/90 backdrop-blur-sm p-2 rounded-full hover:bg-white transition-colors"
          >
            <Share2 className="w-4 h-4 text-gray-700" />
          </button>
        </div>
        
        {/* Downloaded Badge */}
        <div className="absolute bottom-3 left-3">
          <span className="bg-green-600 text-white px-3 py-1 rounded-full text-xs font-medium">
            ✓ Downloaded
          </span>
        </div>
      </div>

      {/* Article Content */}
      <div className="p-4">
        <h3 className={`text-lg font-semibold mb-2 leading-snug ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          {article.title}
        </h3>
        <p className={`text-sm mb-3 line-clamp-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {article.content}
        </p>

        {/* Source and Duration */}
        <div className={`flex items-center text-xs mb-2 gap-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <span>{article.source}</span>
          <span>•</span>
          <span>{article.duration}</span>
          {article.downloadedAt && (
            <>
              <span>•</span>
              <span>Downloaded {new Date(article.downloadedAt).toLocaleDateString()}</span>
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-4 gap-2">
          <Button
            onClick={() => onPlaySummary(article)}
            className="bg-blue-700 hover:bg-blue-800 text-white flex items-center justify-center gap-1 py-5 text-xs"
          >
            {isPlayingSummary ? (
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="4" width="4" height="16" />
                <rect x="14" y="4" width="4" height="16" />
              </svg>
            ) : (
              <Play className="w-3 h-3" fill="currentColor" />
            )}
            <span>Summary</span>
          </Button>
          <Button
            onClick={() => onPlayFull(article)}
            variant="outline"
            className={`flex items-center justify-center gap-1 py-5 text-xs ${
              darkMode 
                ? 'border-blue-500 text-blue-400 hover:bg-blue-900/30' 
                : 'border-blue-700 text-blue-700 hover:bg-blue-50'
            }`}
          >
            {isPlayingFull ? (
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="4" width="4" height="16" />
                <rect x="14" y="4" width="4" height="16" />
              </svg>
            ) : (
              <Play className="w-3 h-3" />
            )}
            <span>Full</span>
          </Button>
          <Button
            onClick={() => onTextRead(article)}
            variant="outline"
            className={`flex items-center justify-center py-5 ${
              darkMode 
                ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <BookOpen className="w-4 h-4" />
          </Button>
          <Button
            onClick={() => onDelete(article)}
            variant="outline"
            className={`flex items-center justify-center py-5 ${
              darkMode 
                ? 'border-red-500 text-red-400 hover:bg-red-900/30' 
                : 'border-red-300 text-red-600 hover:bg-red-50'
            }`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default LibraryCard;